import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header as UIHeader, HeaderLogo } from '@voilajsx/uikit/header';
import { Button } from '@voilajsx/uikit/button';
import {
  Home,
  Users,
  BookOpen,
  Award,
  Phone,
  LogIn,
  LayoutDashboard,
  LogOut,
  FileText,
  Shield,
  ChevronDown,
  Image,
  User,
  Settings
} from 'lucide-react';
import { asset } from '../utils/asset';
import { useAuth } from '../../features/auth';

// About dropdown menu items (Our Team & Success Stories moved to top level)
const aboutMenuItems = [
  { key: 'about-us', label: 'About Us', href: '/about', icon: Users },
  { key: 'services', label: 'Services', href: '/services', icon: BookOpen },
];

// Admin dropdown menu items (only existing pages)
const adminMenuItems = [
  { key: 'admin-dashboard', label: 'Admin Dashboard', href: '/admin', icon: LayoutDashboard },
  { key: 'manage-materials', label: 'Manage Materials', href: '/materials', icon: FileText },
  { key: 'manage-events', label: 'Manage Events', href: '/events', icon: Image },
  { key: 'manage-team', label: 'Manage Team', href: '/team/admin', icon: Users },
  { key: 'manage-stories', label: 'Manage Success Stories', href: '/achievements/admin', icon: Award },
  { key: 'manage-settings', label: 'Manage Settings', href: '/settings', icon: Settings },
  { key: 'logout', label: 'Logout', href: '/auth/logout', icon: LogOut },
];

// User dropdown menu items (only existing pages)
const userMenuItems = [
  { key: 'my-dashboard', label: 'My Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { key: 'my-materials', label: 'Study Materials', href: '/materials', icon: FileText },
  { key: 'logout', label: 'Logout', href: '/auth/logout', icon: LogOut },
];

// Tuition Point Logo component
const TuitionPointLogo: React.FC = () => (
  <div className="flex items-center gap-3">
   <img
      src={asset('tuitionpoint_logo.png')}
      alt="Tuition Point"
      className="h-20 py-1.5 rounded-md"
    />
  </div>
);

// Generic Dropdown Component
interface DropdownProps {
  title: string;
  icon: React.ComponentType<any>;
  menuItems: typeof adminMenuItems;
  buttonClassName?: string;
  alignLeft?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({ title, icon: Icon, menuItems, buttonClassName = '', alignLeft = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(prev => (prev + 1) % menuItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(prev => prev <= 0 ? menuItems.length - 1 : prev - 1);
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      navigate(menuItems[activeIndex].href);
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const isCurrentPath = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const hasActivePath = menuItems.some(item => isCurrentPath(item.href));

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={`flex items-center gap-2 px-4 py-2 rounded-md text-foreground hover:bg-gray-100 transition-colors ${
          hasActivePath ? 'bg-gray-100' : ''
        } ${buttonClassName}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Icon className="h-4 w-4" />
        <span className="font-medium">{title}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute top-full ${alignLeft ? 'left-0' : 'right-0'} mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50`}>
          {menuItems.map((item, index) => {
            const ItemIcon = item.icon;
            const isCurrent = isCurrentPath(item.href);
            const isActive = index === activeIndex;

            return (
              <button
                key={item.key}
                onClick={() => {
                  navigate(item.href);
                  setIsOpen(false);
                  setActiveIndex(-1);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                  isCurrent ? 'bg-cyan-50 text-cyan-600 border-r-2 border-cyan-600' : 'text-gray-700'
                } ${isActive ? 'bg-gray-100' : ''}`}
              >
                <ItemIcon className={`h-4 w-4 ${isCurrent ? 'text-cyan-600' : 'text-gray-500'}`} />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Navigation Link Component
const NavLink: React.FC<{ href: string; icon: React.ComponentType<any>; label: string; currentPath: string }> = ({
  href, icon: Icon, label, currentPath
}) => {
  const navigate = useNavigate();
  const isActive = currentPath === href || (href !== '/' && currentPath.startsWith(href));

  return (
    <button
      onClick={() => navigate(href)}
      className={`flex items-center gap-2 px-4 py-2 rounded-md text-foreground hover:bg-gray-100 transition-colors ${
        isActive ? 'bg-gray-100' : ''
      }`}
    >
      <Icon className="h-4 w-4" />
      <span className="font-medium">{label}</span>
    </button>
  );
};

/**
 * Tuition Point Header Component.
 * Enforces a maximum width on the content for consistency across the application.
 */
export const Header: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  return (
    <UIHeader
      tone="clean"
      size="2xl"
      position="sticky"
      className="header-nav shadow-soft"
      style={{background: "#FFFFFF"}}
    >
        <div className="container mx-auto px-4 max-w-7xl flex justify-between items-center w-full py-0 h-full">
            <HeaderLogo>
                <TuitionPointLogo />
            </HeaderLogo>

            {/* All navigation items together - right aligned */}
            <div className="flex items-center gap-1">
              <NavLink href="/" icon={Home} label="Home" currentPath={location.pathname} />
              <Dropdown
                title="About"
                icon={Users}
                menuItems={aboutMenuItems}
                alignLeft={true}
              />
              <NavLink href="/team" icon={Users} label="Our Team" currentPath={location.pathname} />
              <NavLink href="/achievements" icon={Award} label="Success Stories" currentPath={location.pathname} />
              <NavLink href="/materials" icon={FileText} label="Materials" currentPath={location.pathname} />
              <NavLink href="/events" icon={Image} label="Gallery" currentPath={location.pathname} />
              {isAuthenticated && (
                <NavLink href="/dashboard" icon={LayoutDashboard} label="Dashboard" currentPath={location.pathname} />
              )}
              <NavLink href="/contact" icon={Phone} label="Contact" currentPath={location.pathname} />
              {/* Login or Admin/User dropdown based on auth status */}
              {!isAuthenticated ? (
                <NavLink href="/auth/login" icon={LogIn} label="Login" currentPath={location.pathname} />
              ) : (
                <>
                  {user?.role === 'admin' && (
                    <Dropdown title="Admin" icon={Shield} menuItems={adminMenuItems} />
                  )}
                  {user?.role !== 'admin' && (
                    <Dropdown title="My Account" icon={User} menuItems={userMenuItems} />
                  )}
                </>
              )}
              {/* Enroll Now button - always visible */}
              <Button asChild className="btn-cyan bg-cyan-500 hover:bg-cyan-600 text-white ml-2">
                <a href="/enrollment">Enroll Now</a>
              </Button>
            </div>
        </div>
    </UIHeader>
  );
};

// Export PublicHeader as alias for backward compatibility
export const PublicHeader = Header;
